/*
 * Copyright (c) 2026 CTO's Club LLC. All rights reserved.
 * Author: Alex Baretta (alex@baretta.com).
 */

export const DefaultSpaBundleRevalidationIntervalMs: number = 30_000;
export const SpaBundleReloadAttemptStorageKey: string =
    'anchorbase.portal.spaBundleReloadAttempt';

export type SpaBundleRevalidationResult =
    | 'fetch_failed'
    | 'in_flight'
    | 'missing_current_entrypoint'
    | 'missing_served_entrypoint'
    | 'reloaded'
    | 'reload_suppressed'
    | 'throttled'
    | 'unchanged';

export type SpaBundleRevalidationState = {
    inFlight: Promise<SpaBundleRevalidationResult> | null;
    lastCheckStartedAtMs: number | null;
};

export type SpaBundleRevalidationEnvironment = {
    readonly document: Document;
    readonly fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    readonly locationHref: string;
    readonly now: () => number;
    readonly reload: () => void;
    readonly sessionStorage: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
};

export type SpaBundleRevalidationOptions = {
    readonly minIntervalMs?: number;
};

export function createSpaBundleRevalidationState(): SpaBundleRevalidationState {
    return {
        inFlight: null,
        lastCheckStartedAtMs: null,
    };
}

export function createBrowserSpaBundleRevalidationEnvironment(): SpaBundleRevalidationEnvironment {
    return {
        document: window.document,
        fetch: window.fetch.bind(window),
        locationHref: window.location.href,
        now: () => Date.now(),
        reload: () => {
            window.location.reload();
        },
        sessionStorage: window.sessionStorage,
    };
}

export function getCurrentSpaBundleEntrypoint(
    documentValue: Document,
    locationHref: string
): string | null {
    return findSpaBundleEntrypoint(documentValue, locationHref);
}

export function getServedSpaBundleEntrypoint(
    htmlText: string,
    locationHref: string
): string | null {
    const parsedDocument: Document = new DOMParser().parseFromString(htmlText, 'text/html');
    return findSpaBundleEntrypoint(parsedDocument, locationHref);
}

export async function revalidateSpaBundleForNavigation(
    environment: SpaBundleRevalidationEnvironment,
    state: SpaBundleRevalidationState,
    options: SpaBundleRevalidationOptions = {}
): Promise<SpaBundleRevalidationResult> {
    const now: number = environment.now();
    const minIntervalMs: number =
        options.minIntervalMs ?? DefaultSpaBundleRevalidationIntervalMs;

    if (state.inFlight !== null) {
        return 'in_flight';
    }

    if (
        state.lastCheckStartedAtMs !== null &&
        now - state.lastCheckStartedAtMs < minIntervalMs
    ) {
        return 'throttled';
    }

    const revalidationPromise: Promise<SpaBundleRevalidationResult> =
        runSpaBundleRevalidation(environment);
    state.lastCheckStartedAtMs = now;
    state.inFlight = revalidationPromise;

    try {
        return await revalidationPromise;
    } finally {
        if (state.inFlight === revalidationPromise) {
            state.inFlight = null;
        }
    }
}

async function runSpaBundleRevalidation(
    environment: SpaBundleRevalidationEnvironment
): Promise<SpaBundleRevalidationResult> {
    const currentEntrypoint: string | null = getCurrentSpaBundleEntrypoint(
        environment.document,
        environment.locationHref
    );
    if (currentEntrypoint === null) {
        return 'missing_current_entrypoint';
    }

    let response: Response;
    try {
        response = await environment.fetch(buildRevalidationUrl(environment.locationHref), {
            cache: 'no-cache',
            credentials: 'same-origin',
        });
    } catch {
        return 'fetch_failed';
    }

    if (!response.ok) {
        return 'fetch_failed';
    }

    let responseHtml: string;
    try {
        responseHtml = await response.text();
    } catch {
        return 'fetch_failed';
    }

    const servedEntrypoint: string | null = getServedSpaBundleEntrypoint(
        responseHtml,
        environment.locationHref
    );
    if (servedEntrypoint === null) {
        return 'missing_served_entrypoint';
    }

    if (currentEntrypoint === servedEntrypoint) {
        environment.sessionStorage.removeItem(SpaBundleReloadAttemptStorageKey);
        return 'unchanged';
    }

    const previousReloadAttempt: string | null = environment.sessionStorage.getItem(
        SpaBundleReloadAttemptStorageKey
    );
    if (previousReloadAttempt === servedEntrypoint) {
        return 'reload_suppressed';
    }

    environment.sessionStorage.setItem(
        SpaBundleReloadAttemptStorageKey,
        servedEntrypoint
    );
    environment.reload();
    return 'reloaded';
}

function findSpaBundleEntrypoint(
    documentValue: Document,
    locationHref: string
): string | null {
    const scriptElements: HTMLScriptElement[] = Array.from(
        documentValue.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
    );
    const scriptElement: HTMLScriptElement | undefined = scriptElements[0];
    if (scriptElement === undefined) {
        return null;
    }

    return normalizeEntrypointPath(scriptElement.getAttribute('src'), locationHref);
}

function normalizeEntrypointPath(src: string | null, locationHref: string): string | null {
    if (src === null || src.trim() === '') {
        return null;
    }

    try {
        const scriptUrl: URL = new URL(src, locationHref);
        return `${scriptUrl.pathname}${scriptUrl.search}`;
    } catch {
        return null;
    }
}

function buildRevalidationUrl(locationHref: string): URL {
    return new URL('/', locationHref);
}
